import React, { useEffect, useState } from 'react';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import ProductsSection from './components/ProductsSection';
import ServicesSection from './components/ServicesSection';
import AboutSection from './components/AboutSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import ChatBot from './components/ChatBot';
import ShoppingCart from './components/ShoppingCart';
import Checkout from './components/Checkout';
import DespreProiect from './components/DespreProiect';
import { CartProvider } from './context/CartContext';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('home'); // 'home' or 'despre-proiect'

  // Loading Screen
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Page navigation function
  const navigateToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  // Loading Screen Component
  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center animated-gradient">
        <div className="text-center">
          {/* Logo Animation */}
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-white/10 backdrop-blur-sm border-2 border-white/20 flex items-center justify-center pulse-glow">
              <div className="text-4xl font-bold text-white font-display">B</div>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-display fade-in-up elegant-glow">
              BOTEZ DE POVESTE
            </h1>
            
            <p className="text-white/80 text-lg mb-8 fade-in-up stagger-1">
              Se încarcă experiența magică...
            </p>
          </div>

          {/* Loading Animation */}
          <div className="flex justify-center items-center space-x-3 mb-8">
            <div className="loading-dots">
              <span className="w-3 h-3 bg-white/80 rounded-full inline-block"></span>
              <span className="w-3 h-3 bg-white/80 rounded-full inline-block"></span>
              <span className="w-3 h-3 bg-white/80 rounded-full inline-block"></span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-72 mx-auto">
            <div className="w-full bg-white/10 rounded-full h-1 backdrop-blur-sm">
              <div 
                className="bg-gradient-to-r from-white via-gold-300 to-white h-1 rounded-full transition-all duration-2000 ease-out animate-shimmer" 
                style={{ width: '100%' }}
              ></div>
            </div>
          </div>

          {/* Loading text */}
          <p className="text-white/60 text-sm mt-4 animate-pulse">
            Pregătim totul pentru o experiență de neuitat...
          </p>
        </div>
      </div>
    );
  }

  return (
    <CartProvider>
      <div className="App min-h-screen bg-white relative overflow-x-hidden">
        {/* Scroll Progress Indicator */}
        <div className="fixed top-0 left-0 w-full h-1 bg-transparent z-50">
          <div 
            className="h-full bg-gradient-to-r from-navy-600 via-gold-500 to-emerald-600 transition-all duration-300 ease-out shadow-lg"
            style={{
              width: `${(window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100}%`
            }}
          />
        </div>

        {/* Header - stays on all pages */}
        <Header currentPage={currentPage} navigateToPage={navigateToPage} />
        
        {/* Content based on current page */}
        {currentPage === 'home' ? (
          <div className="fade-in">
            <main className="relative">
              <HeroSection />
              
              {/* Section Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-navy-200 to-transparent mx-auto max-w-4xl" />
              
              <ProductsSection />
              
              {/* Section Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-gold-200 to-transparent mx-auto max-w-4xl" />
              
              <ServicesSection />
              
              {/* Section Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-gold-200 to-transparent mx-auto max-w-4xl" />
              
              <AboutSection />
              
              {/* Section Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-emerald-200 to-transparent mx-auto max-w-4xl" />
              
              <ContactSection />
            </main>
          </div>
        ) : (
          <DespreProiect navigateToPage={navigateToPage} />
        )}
        
        {/* Footer - stays on all pages */}
        <Footer />

        {/* Global Floating Elements */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          {/* Ambient Particles */}
          {[...Array(6)].map((_, i) => (
            <div
              key={`ambient-${i}`}
              className={`absolute rounded-full opacity-10 ${
                i % 3 === 0 ? 'w-1 h-1 bg-navy-400' : 
                i % 3 === 1 ? 'w-1.5 h-1.5 bg-gold-400' : 
                'w-0.5 h-0.5 bg-emerald-400'
              } ${i % 2 === 0 ? 'particle-1' : 'particle-2'}`}
              style={{
                left: `${15 + Math.random() * 70}%`,
                top: `${15 + Math.random() * 70}%`,
                animationDelay: `${Math.random() * 12}s`,
                animationDuration: `${20 + Math.random() * 10}s`
              }}
            />
          ))}

          {/* Gradient Orbs */}
          <div className="absolute top-1/4 -left-32 w-64 h-64 bg-gradient-to-r from-navy-100/5 to-gold-100/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-gradient-to-r from-emerald-100/5 to-navy-100/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '4s'}} />
        </div>

        {/* Shopping Cart Sidebar */}
        <ShoppingCart onCheckout={() => setIsCheckoutOpen(true)} />
        
        {/* Checkout Modal */}
        <Checkout 
          isOpen={isCheckoutOpen} 
          onClose={() => setIsCheckoutOpen(false)}
        />

        {/* Chat Bot Component */}
        <ChatBot />
      </div>
    </CartProvider>
  );
}

export default App;
